/**
 * Which of a job's files a re-run can resubmit, and the submit `files[]` entry
 * each becomes. Pure, so the rule is testable without the service's Supabase
 * client.
 *
 * The rule is the worker's `downloadFile`, read backwards. The worker opens a
 * file one of three ways — SharePoint drive + item ids, a SharePoint web URL,
 * or a Storage object named by bucket AND path — and throws for anything else.
 * So a row is re-runnable only when it carries one of those three pointer sets
 * whole, and the entry carries every pointer column the row has.
 *
 * Why this exists (2026-10-09): the re-run copied `storage_path` but not
 * `storage_bucket`, so a seller-financials job (whose files live in a private
 * bucket) re-ran into "has no SharePoint pointers and no storage object" every
 * time, and the listing-watch feed jobs (migration 275) would have done the same.
 */

export type RerunFileSource = 'attachment' | 'inline_link' | 'dashboard_upload';

export interface RerunFileRow {
  file_name: string;
  file_size?: number | null;
  file_type?: string | null;
  file_extension?: string | null;
  sharepoint_item_id?: string | null;
  sharepoint_drive_id?: string | null;
  sharepoint_web_url?: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
  content_id?: string | null;
}

export interface RerunFileEntry {
  file_name: string;
  file_size?: number;
  file_type?: string;
  file_extension?: string;
  sharepoint_item_id?: string;
  sharepoint_drive_id?: string;
  sharepoint_web_url?: string;
  storage_bucket?: string;
  storage_path?: string;
  content_id?: string;
  source: RerunFileSource;
}

/** The columns a re-run must select to rebuild every pointer the worker reads. */
export const RERUN_FILE_COLUMNS =
  'file_name, file_size, file_type, file_extension, sharepoint_item_id, sharepoint_drive_id, sharepoint_web_url, storage_bucket, storage_path, content_id';

/** True when the worker could open this row: one of its three pointer sets is whole. */
export function isRerunnable(row: RerunFileRow): boolean {
  if (row.sharepoint_drive_id && row.sharepoint_item_id) return true;
  if (row.sharepoint_web_url) return true;
  if (row.storage_bucket && row.storage_path) return true;
  return false;
}

const str = (v: string | null | undefined): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);

/** The submit entries for a job's re-runnable files, pointers intact. */
export function rerunnableFiles(rows: readonly RerunFileRow[]): RerunFileEntry[] {
  return rows.filter(isRerunnable).map((f) => ({
    file_name: f.file_name,
    file_size: typeof f.file_size === 'number' ? f.file_size : undefined,
    file_type: str(f.file_type),
    file_extension: str(f.file_extension),
    sharepoint_item_id: str(f.sharepoint_item_id),
    sharepoint_drive_id: str(f.sharepoint_drive_id),
    sharepoint_web_url: str(f.sharepoint_web_url),
    storage_bucket: str(f.storage_bucket),
    storage_path: str(f.storage_path),
    content_id: str(f.content_id),
    source: 'dashboard_upload',
  }));
}
